import Button from "@components/blocks/Button";
import NumberInput from "@components/hookform/NumberInput";
import ControlledSelect from "@components/hookform/Select";
import { fetchGetAssumptionsWithLogging } from "@lib/api-wrappers";
import { ComponentSectionsBudgetCalculator } from "@lib/generated/graphql";
import { getFederalStateOptions } from "@lib/labels";
import clsx from "clsx";
import { FC, useCallback, useEffect, useState } from "react";
import { DeepPartial, useForm } from "react-hook-form";
import BudgetCalculatorResult from "./BudgetCalculatorResult";
import calculateBudget from "./calculateBudget";
import { BudgetCalculatorForm, BudgetResult } from "./types";
export interface BudgetCalculatorProps {
  data: ComponentSectionsBudgetCalculator;
  className?: string;
}
const federalStateOptions = getFederalStateOptions();
const BudgetCalculator: FC<BudgetCalculatorProps> = ({
  data,
  className
}) => {
  const {
    control,
    watch,
    getValues
  } = useForm<BudgetCalculatorForm>({
    defaultValues: {
      repaymentRate: 1.5,
      interestRate: 3.5,
      federalState: "BERLIN",
      equity: 50000,
      availableAmountMonthly: 1500
    },
    mode: "onChange"
  });
  const [result, setResult] = useState<BudgetResult>();
  const onSubmit = useCallback(async (values: DeepPartial<BudgetCalculatorForm>) => {
    if (!(values.interestRate && values.federalState && values.availableAmountMonthly && values.repaymentRate)) {
      setResult(undefined);
      return;
    }
    try {
      const assumptions = await fetchGetAssumptionsWithLogging({
        queryParams: {
          federalState: values.federalState
        }
      });
      setResult(calculateBudget(values as BudgetCalculatorForm, assumptions));
    } catch {
      setResult(undefined);
    }
  }, []);
  useEffect(() => {
    onSubmit(getValues());
  }, [getValues, onSubmit]);
  useEffect(() => {
    const subscription = watch(values_0 => onSubmit(values_0));
    return () => subscription.unsubscribe();
  }, [onSubmit, watch]);
  return <div className={clsx("py-m px-s m:p-m border bg-neutral-12 border-neutral-100 rounded-2xs", className)}>
      {<>
          <h2 className="text-h2 mb-2xs">{data.title}</h2>
          <p className="text-body-2 mb-3s">{data.description}</p>
          <form onSubmit={e => e.preventDefault()} className="space-y-2s mb-s">
            <div className="flex flex-col m:flex-row gap-y-2s gap-s">
              <NumberInput control={control} name="availableAmountMonthly" suffix="€" label="Monatlich verfügbarer Betrag" />
              <NumberInput control={control} name="equity" suffix="€" label="Einzusetzendes Eigenkapital" />
            </div>
            <div className="flex flex-col m:flex-row gap-y-2s gap-s">
              <NumberInput control={control} name="repaymentRate" suffix="%" label="Anfängliche Tilgung" />
              <NumberInput control={control} name="interestRate" suffix="%" label="Sollzinssatz" />
            </div>

            <ControlledSelect name="federalState" control={control} size="md" label="In welchem Bundesland möchtest du kaufen?" options={federalStateOptions} />
          </form>
          <BudgetCalculatorResult result={result} />
          {data.button && <Button {...data.button} className="!w-full mt-3s" />}
        </>}
    </div>;
};
export default BudgetCalculator;